// Knowledge Base API (Tier 5.8).
//
// `apiFetch` returns the raw response envelope ({ success, message, data }); this
// layer unwraps `.data` and maps snake_case → camelCase, per the settled
// api-envelope convention. FAQ/Wiki/Help authoring is platform-admin only
// (server-enforced); the media / system-event / what's-new reads are firm-facing
// projections over apps.platform content.

import { apiFetch } from "@/lib/api";
import type {
  Faq,
  FaqInput,
  HelpNode,
  KbMediaAsset,
  KbSystemEvent,
  SearchResults,
  WhatsNewArchiveEntry,
  WikiPage,
  WikiPageInput,
} from "./types";

interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
}

// --- mappers -----------------------------------------------------------------

interface ApiFaq {
  id: string;
  question: string;
  answer: string;
  display_order: number;
}

function toFaq(a: ApiFaq): Faq {
  return { id: a.id, question: a.question, answer: a.answer, displayOrder: a.display_order };
}

function fromFaqInput(input: FaqInput): Record<string, unknown> {
  return { question: input.question, answer: input.answer, display_order: input.displayOrder };
}

interface ApiWikiPage {
  id: string;
  entry_type: string;
  page_title: string;
  page_slug: string;
  content: string;
  created_at: string;
  updated_at: string;
}

function toWikiPage(a: ApiWikiPage): WikiPage {
  return {
    id: a.id,
    entryType: a.entry_type,
    pageTitle: a.page_title,
    pageSlug: a.page_slug,
    content: a.content,
    createdAt: a.created_at,
    updatedAt: a.updated_at,
  };
}

function fromWikiInput(input: WikiPageInput): Record<string, unknown> {
  return {
    entry_type: input.entryType,
    page_title: input.pageTitle,
    page_slug: input.pageSlug,
    content: input.content,
  };
}

interface ApiMediaAsset {
  id: string;
  parent: string | null;
  display_seq: number;
  asset_type: KbMediaAsset["assetType"];
  symlink_target: string | null;
  description: string;
  slug: string;
  video_id: string;
  href: string;
  detail: string;
}

function toMediaAsset(a: ApiMediaAsset): KbMediaAsset {
  return {
    id: a.id,
    parent: a.parent,
    displaySeq: a.display_seq,
    assetType: a.asset_type,
    symlinkTarget: a.symlink_target,
    description: a.description,
    slug: a.slug,
    videoId: a.video_id,
    href: a.href,
    detail: a.detail,
  };
}

interface ApiSystemEvent {
  id: string;
  kind: string;
  display_seq: number;
  title: string;
  href: string;
  detail: string;
  badge: string;
  new_tab: boolean;
}

function toSystemEvent(a: ApiSystemEvent): KbSystemEvent {
  return {
    id: a.id,
    kind: a.kind,
    displaySeq: a.display_seq,
    title: a.title,
    href: a.href,
    detail: a.detail,
    badge: a.badge,
    newTab: a.new_tab,
  };
}

// --- global search -----------------------------------------------------------

export async function searchKb(q: string): Promise<SearchResults> {
  const { data } = await apiFetch<Envelope<{ faq: SearchResults["faq"]; wiki: SearchResults["wiki"] }>>(
    `/kb/search/?q=${encodeURIComponent(q)}`,
  );
  return data;
}

// --- FAQ ---------------------------------------------------------------------

export async function listFaqs(search = ""): Promise<Faq[]> {
  const qs = search ? `?search=${encodeURIComponent(search)}&page_size=200` : "?page_size=200";
  const { data } = await apiFetch<Envelope<ApiFaq[]>>(`/kb/faqs/${qs}`);
  return data.map(toFaq);
}

export async function createFaq(input: FaqInput): Promise<Faq> {
  const { data } = await apiFetch<Envelope<ApiFaq>>("/kb/faqs/", {
    method: "POST",
    json: fromFaqInput(input),
  });
  return toFaq(data);
}

export async function updateFaq(id: string, input: FaqInput): Promise<Faq> {
  const { data } = await apiFetch<Envelope<ApiFaq>>(`/kb/faqs/${id}/`, {
    method: "PATCH",
    json: fromFaqInput(input),
  });
  return toFaq(data);
}

export async function deleteFaq(id: string): Promise<void> {
  await apiFetch(`/kb/faqs/${id}/`, { method: "DELETE" });
}

// --- Wiki --------------------------------------------------------------------

export async function listWikiPages(): Promise<WikiPage[]> {
  const { data } = await apiFetch<Envelope<ApiWikiPage[]>>("/kb/wiki-pages/?page_size=200");
  return data.map(toWikiPage);
}

export async function getWikiBySlug(slug: string): Promise<WikiPage> {
  const { data } = await apiFetch<Envelope<ApiWikiPage>>(`/kb/wiki-pages/by-slug/${slug}/`);
  return toWikiPage(data);
}

export async function createWikiPage(input: WikiPageInput): Promise<WikiPage> {
  const { data } = await apiFetch<Envelope<ApiWikiPage>>("/kb/wiki-pages/", {
    method: "POST",
    json: fromWikiInput(input),
  });
  return toWikiPage(data);
}

export async function updateWikiPage(id: string, input: Partial<WikiPageInput>): Promise<WikiPage> {
  const body: Record<string, unknown> = {};
  if (input.entryType !== undefined) body.entry_type = input.entryType;
  if (input.pageTitle !== undefined) body.page_title = input.pageTitle;
  if (input.pageSlug !== undefined) body.page_slug = input.pageSlug;
  if (input.content !== undefined) body.content = input.content;
  const { data } = await apiFetch<Envelope<ApiWikiPage>>(`/kb/wiki-pages/${id}/`, {
    method: "PATCH",
    json: body,
  });
  return toWikiPage(data);
}

export async function deleteWikiPage(id: string): Promise<void> {
  await apiFetch(`/kb/wiki-pages/${id}/`, { method: "DELETE" });
}

// --- Help nodes --------------------------------------------------------------

interface ApiHelpNode {
  node_key: string;
  lang: string;
  help_text: string;
  help_text_en: string;
  help_text_es: string;
}

function toHelpNode(a: ApiHelpNode): HelpNode {
  return {
    nodeKey: a.node_key,
    lang: a.lang,
    helpText: a.help_text,
    helpTextEn: a.help_text_en,
    helpTextEs: a.help_text_es,
  };
}

export async function getHelpNode(nodeKey: string, lang = "en"): Promise<HelpNode> {
  const { data } = await apiFetch<Envelope<ApiHelpNode>>(
    `/kb/help-nodes/${nodeKey}/?lang=${lang}`,
  );
  return toHelpNode(data);
}

export async function saveHelpNode(
  nodeKey: string,
  input: { helpTextEn?: string; helpTextEs?: string },
): Promise<HelpNode> {
  const body: Record<string, unknown> = {};
  if (input.helpTextEn !== undefined) body.help_text_en = input.helpTextEn;
  if (input.helpTextEs !== undefined) body.help_text_es = input.helpTextEs;
  const { data } = await apiFetch<Envelope<ApiHelpNode>>(`/kb/help-nodes/${nodeKey}/`, {
    method: "PATCH",
    json: body,
  });
  return toHelpNode(data);
}

// --- Media (firm-read over platform) -----------------------------------------

export async function listMediaBySlug(slug: string): Promise<KbMediaAsset[]> {
  const { data } = await apiFetch<Envelope<ApiMediaAsset[]>>(`/kb/media/by-slug/${slug}/`);
  return data.map(toMediaAsset);
}

export async function listMedia(parent?: string, search = ""): Promise<KbMediaAsset[]> {
  const params = new URLSearchParams();
  if (parent !== undefined) params.set("parent", parent);
  if (search) params.set("search", search);
  const qs = params.toString();
  const { data } = await apiFetch<Envelope<ApiMediaAsset[]>>(`/kb/media/${qs ? `?${qs}` : ""}`);
  return data.map(toMediaAsset);
}

// --- System events (toolbox + videos) ----------------------------------------

export async function listSystemEvents(type: string): Promise<KbSystemEvent[]> {
  const { data } = await apiFetch<Envelope<ApiSystemEvent[]>>(
    `/kb/system-events/?type=${encodeURIComponent(type)}`,
  );
  return data.map(toSystemEvent);
}

// --- What's New --------------------------------------------------------------

export async function listWhatsNewArchive(search = ""): Promise<WhatsNewArchiveEntry[]> {
  const qs = search ? `?search=${encodeURIComponent(search)}&page_size=200` : "?page_size=200";
  const { data } = await apiFetch<Envelope<
    { id: string; title: string; body: string; created_at: string }[]
  >>(`/kb/whats-new/archive/${qs}`);
  return data.map((a) => ({ id: a.id, title: a.title, body: a.body, createdAt: a.created_at }));
}

export async function getWhatsNewCurrent(): Promise<{ current: string; publishedAt: string | null }> {
  const { data } = await apiFetch<Envelope<{ current: string; published_at: string | null }>>(
    "/kb/whats-new/current/",
  );
  return { current: data.current, publishedAt: data.published_at };
}
